import { useEffect, useState } from "react";
import {
  Box, Stack, Typography, Chip, Alert, CircularProgress, IconButton, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, InputAdornment,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { listAdmins, addAdmin, removeAdmin, AdminRow } from "../services/access";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Access() {
  const [rows,    setRows]    = useState<AdminRow[]>([]);
  const [domain,  setDomain]  = useState("cucuru.com");
  // Habilitar personas está restringido a los granters (prd: pga). El backend lo
  // hace cumplir con 403; acá sólo se muestra u oculta el formulario.
  const [canGrant, setCanGrant] = useState(false);
  const [granters, setGranters] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [ok,      setOk]      = useState<string | null>(null);
  const [newLocal, setNewLocal] = useState("");
  const [confirm, setConfirm] = useState<AdminRow | null>(null);

  const me = (localStorage.getItem("webadmin2.email") ?? "").toLowerCase();

  async function load() {
    setLoading(true); setErr(null);
    try {
      const r = await listAdmins();
      setRows(r.items); setDomain(r.domain); setCanGrant(r.canGrant); setGranters(r.granters);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const localValid = /^[a-z0-9._-]+$/i.test(newLocal.trim());
  // Los deshabilitados no se muestran (pedido 2026-09-05); re-habilitar = volver
  // a cargarlos desde "Habilitar persona" (sólo granters).
  const visibles = rows.filter((r) => r.allowed);
  const ocultos  = rows.length - visibles.length;

  async function onAdd() {
    if (!localValid) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      await addAdmin(`${newLocal.trim()}@${domain}`);
      setOk(`${newLocal.trim()}@${domain} habilitado. Al primer login se le va a pedir enrolar el 2FA.`);
      setNewLocal("");
      await load();
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  async function onToggle(row: AdminRow, enable: boolean) {
    setBusy(true); setErr(null); setOk(null);
    try {
      if (enable) { await addAdmin(row.email); setOk(`${row.email} re-habilitado.`); }
      else        { await removeAdmin(row.email); setOk(`${row.email} deshabilitado. Deja de verse en la lista; para re-habilitarlo hay que cargarlo de nuevo.`); }
      setConfirm(null);
      await load();
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Access</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Personas que pueden loguearse como admin a webadmin2 (dominio @{domain}) y estado de su 2FA.
        Una baja conserva el enrolamiento TOTP por si se re-habilita. Cualquier admin puede deshabilitar;
        habilitar sólo {granters.length ? granters.join(", ") : "los granters configurados"}.
      </Typography>

      {canGrant ? (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Habilitar persona</Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField size="small" label="Usuario" value={newLocal} disabled={busy}
            onChange={(e) => setNewLocal(e.target.value.trim())}
            error={newLocal.trim() !== "" && !localValid}
            InputProps={{ endAdornment: <InputAdornment position="end">@{domain}</InputAdornment> }}
            sx={{ width: 320 }} />
          <Button variant="contained" startIcon={<PersonAddAlt1Icon />} onClick={onAdd}
            disabled={busy || !localValid}>
            Habilitar
          </Button>
        </Stack>
      </Paper>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sólo {granters.length ? granters.join(", ") : "los granters configurados"} puede habilitar personas.
          Vos podés deshabilitar.
        </Alert>
      )}

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {ok  && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        {ocultos > 0 && (
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            {ocultos} {ocultos === 1 ? "persona deshabilitada oculta" : "personas deshabilitadas ocultas"}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refrescar">
          <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Acceso</TableCell>
              <TableCell>2FA</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Enrolado el</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && visibles.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {visibles.map((r) => (
              <TableRow key={r.email} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {r.email}{r.email.toLowerCase() === me && (
                    <Chip label="vos" size="small" sx={{ ml: 1, height: 20, fontSize: 11 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={r.allowed ? "HABILITADO" : "DESHABILITADO"}
                    sx={{ fontWeight: 700, fontSize: 11, height: 22,
                          color: r.allowed ? "#166534" : "#B91C1C",
                          bgcolor: r.allowed ? "#DCFCE7" : "#FEE2E2" }} />
                  {r.source === "config" && (
                    <Tooltip title="Viene del bootstrap del template (MfaAllowedIdentities). Al tocarlo desde acá pasa a gestionarse por el ABM.">
                      <Chip label="config" size="small" variant="outlined"
                        sx={{ ml: 0.5, height: 20, fontSize: 10 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={r.enrolled ? "ENROLADO" : "SIN ENROLAR"}
                    sx={{ fontWeight: 700, fontSize: 11, height: 22,
                          color: r.enrolled ? "#1E40AF" : "#9A6700",
                          bgcolor: r.enrolled ? "#DBEAFE" : "#FFF6E0" }} />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12, color: "text.secondary" }}>
                  {fmtDate(r.confirmed_at ?? r.enrolled_at)}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={r.email.toLowerCase() === me
                    ? "No podés deshabilitarte a vos mismo"
                    : "Deshabilitar acceso"}>
                    <span>
                      <IconButton size="small" color="error"
                        disabled={busy || r.email.toLowerCase() === me}
                        onClick={() => setConfirm(r)}>
                        <PersonOffIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>Deshabilitar acceso</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vas a deshabilitar el login de <b>{confirm?.email}</b> a webadmin2. Deja de aparecer en esta
            lista; su enrolamiento 2FA se conserva y sólo {granters.length ? granters.join(", ") : "un granter"} puede
            volver a habilitarlo desde "Habilitar persona". ¿Continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancelar</Button>
          <Button onClick={() => confirm && onToggle(confirm, false)} disabled={busy}
            variant="contained" color="error">
            {busy ? "Deshabilitando…" : "Deshabilitar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
